/**
 * The navigation model — digitaladmin's NavSection[] shape, extended per the
 * plan: badges for live counts, landingRank derived FROM this tree (no parallel
 * LANDING_PRIORITY list that nothing checks), resourceKey linking each item to
 * the permission matrix.
 */
import {
  Megaphone,
  LayoutDashboard,
  AlertOctagon,
  GitPullRequest,
  CalendarDays,
  Ticket,
  BookOpen,
  ShoppingBag,
  BarChart3,
  Settings,
  type LucideIcon,
  ShieldCheck,
} from 'lucide-react';
import type { VocabularyKey } from '@itsm/shared';

export interface NavItem {
  /** The default label. Shown as-is unless `vocabKey` renames it. */
  label: string;
  /**
   * Step 79 — which noun this item IS, when the label is just that noun's
   * plural. The Sidebar resolves it through `useVocab()`, so a tenant
   * renaming "ticket" to "case" gets "Cases" here without a second list of
   * labels to keep in step.
   *
   * Absent on anything whose label is a phrase rather than a noun ("Change
   * calendar", "My approvals"): substituting inside a sentence needs the
   * sentence, and a half-translated phrase reads worse than an untranslated
   * one.
   */
  vocabKey?: VocabularyKey;
  /** Nav labels are plurals by default ("Tickets"). The catalogue is one
   *  thing, not many, so it opts out. */
  vocabPlural?: boolean;
  to: string;
  icon: LucideIcon;
  resourceKey?: string;
  /** Lower = preferred landing page. -1 = never a landing page. */
  landingRank?: number;
  /* `badgeKey` lived here for count bubbles on Inbox / Unassigned / Breaching.
     Those three left the sidebar (Kai) and nothing ever read the field, so it
     went with them rather than sitting as a hook for a feature that does not
     exist. */
}

export interface NavSection {
  heading?: string;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    heading: 'Home',
    items: [
      // Step 50 — landingRank 1 so it is where an agent lands, ahead of the
      // queue: "what needs me?" before "everything".
      { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, resourceKey: 'tickets', landingRank: 1 },
    ],
  },
  {
    heading: 'Work',
    items: [
      {
        label: 'Tickets',
        to: '/tickets',
        vocabKey: 'ticket',
        icon: Ticket,
        resourceKey: 'tickets',
        landingRank: 2,
      },
      // Step 47 — "what is waiting on ME?" is a question the ticket queue
      // cannot answer, because an approver is often not the assignee and may
      // not be in the group at all. It rides the tickets permission: an
      // approver is answering a question about a ticket.
      {
        // Step 51 — Kai: announcements belong on the side menu, not buried in
        // admin. Anyone on the desk can post one; every edit is audited.
        label: 'Announcements',
        to: '/announcements',
        vocabKey: 'announcement',
        icon: Megaphone,
        resourceKey: 'tickets',
        landingRank: -1,
      },
      {
        label: 'My approvals',
        to: '/approvals',
        icon: ShieldCheck,
        resourceKey: 'tickets',
        landingRank: -1,
      },
    ],
  },
  {
    heading: 'ITIL',
    items: [
      // Step 48 — problems are rows in the tickets table but not part of "the
      // queue": no requester is waiting on a reply and the lifecycle differs.
      // Change joins here at Step 49.
      {
        label: 'Problems',
        to: '/problems',
        vocabKey: 'problem',
        icon: AlertOctagon,
        resourceKey: 'tickets',
        landingRank: -1,
      },
      {
        label: 'Changes',
        to: '/changes',
        vocabKey: 'change',
        icon: GitPullRequest,
        resourceKey: 'tickets',
        landingRank: -1,
      },
      {
        label: 'Change calendar',
        to: '/changes/calendar',
        icon: CalendarDays,
        resourceKey: 'tickets',
        landingRank: -1,
      },
    ],
  },
  {
    heading: 'Service',
    items: [
      {
        label: 'Catalogue',
        to: '/catalogue',
        vocabKey: 'catalogue',
        vocabPlural: false,
        icon: ShoppingBag,
        resourceKey: 'catalogue',
        landingRank: 4,
      },
    ],
  },
  {
    heading: 'Knowledge',
    items: [
      {
        label: 'Articles',
        to: '/kb',
        vocabKey: 'article',
        icon: BookOpen,
        resourceKey: 'kb',
        landingRank: 3,
      },
    ],
  },
  {
    heading: 'Insights',
    items: [{ label: 'Reports', to: '/reports', icon: BarChart3, resourceKey: 'reports', landingRank: 5 }],
  },
  {
    // Admin is ONE item opening its own settings shell — the Stripe/Linear
    // pattern; it is what stops this sidebar becoming thirty rows.
    heading: 'Admin',
    items: [{ label: 'Settings', to: '/admin', icon: Settings, resourceKey: 'org-settings', landingRank: 9 }],
  },
];

/** Sections filtered to what this user can see. Permissions arrive at Step 10;
 *  until then callers pass () => true and the shape is already right. */
export function visibleSections(sections: NavSection[], canView: (key: string) => boolean): NavSection[] {
  return sections
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.resourceKey || canView(i.resourceKey)) }))
    .filter((s) => s.items.length > 0);
}
